/**
 * THE FIGURES OF A RE-RUN SAVED INVOCATION — the share page's body, shared since
 * 2026-10-02 with the re-opened saved question (`SavedRunView`), so the two
 * surfaces that re-run a stored invocation render it the same way: a redacted
 * cell with no number, or the metric, its watermark (STALE when the source says
 * so), the table and the deep link.
 *
 * `firstSeenAsOf` is the share page's THEN vs NOW: the first watermark this
 * browser saw, so a re-run can show the movement.
 */
import { History } from 'lucide-react';

import type { AnswerBody } from '../protocol';
import { RedactedCard } from './Cells';
import { DeepLink } from './DeepLink';
import { RawTable } from './ResultTable';

export function AnswerFigures({
  answer,
  firstSeenAsOf = null,
}: {
  answer: Pick<AnswerBody, 'cell' | 'metric_id' | 'as_of' | 'freshness_ok' | 'rows' | 'columns' | 'title' | 'deep_link'>;
  firstSeenAsOf?: string | null;
}) {
  const moved = firstSeenAsOf !== null && answer.as_of !== null && firstSeenAsOf !== answer.as_of;
  if (answer.cell !== null) return <RedactedCard cell={answer.cell} />;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
        {answer.metric_id && <span className="font-mono">{answer.metric_id}</span>}
        {answer.as_of && (
          <span
            className={
              answer.freshness_ok === false
                ? 'inline-flex items-center gap-1 font-medium text-amber-600 dark:text-amber-500'
                : 'inline-flex items-center gap-1'
            }
          >
            <History className="h-3 w-3" aria-hidden />
            {answer.freshness_ok === false ? 'STALE — ' : ''}as of {answer.as_of}
            {moved && firstSeenAsOf !== null && (
              <span className="ml-1 rounded bg-muted px-1.5 py-0.5">
                was {firstSeenAsOf} when you opened this
              </span>
            )}
          </span>
        )}
      </div>
      <RawTable
        rows={(answer.rows ?? []) as Record<string, unknown>[]}
        columns={answer.columns}
        caption={answer.title}
      />
      {answer.deep_link && <DeepLink to={answer.deep_link.href} label={answer.deep_link.label} />}
    </div>
  );
}
